import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { createApi } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../security";
import { Candidate, CandidatesView } from "../candidates/model/candidates";
import {
  AttorneyEmployerFitResponse,
  AttorneyEmployerFitView,
  CreateAttorneyEmployerFitRequest
} from "./model/attorneyEmployerFit";
import {
  CreateJobAttorneyFitRequest,
  DeleteJobAttorneyFitRequest,
  JobAttorneyFitResponse,
  JobAttorneyFitView
} from "./model/jobAttorneyFit";

import {
  Attorney,
  AttorneyResponse,
  AttorneysView
} from "../attorneys/model/attorney";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

// Custom base query with error handling
const customBaseQuery = fetchBaseQuery({
  baseUrl: apiServerUrl,
  prepareHeaders: async (headers) => {
    const token = await sec.getAccessTokenSilently()();
    if (token) headers.set("authorization", `Bearer ${token}`);
    return headers;
  }
});

// Enhanced base query with error notifications
const baseQueryWithErrorHandling = async (
  args: any,
  api: any,
  extraOptions: any
) => {
  const result = await customBaseQuery(args, api, extraOptions);

  // Check for error responses (4xx, 5xx)
  if (result.error) {
    const { status } = result.error;
    const statusCode =
      typeof status === "string" ? parseInt(status, 10) : status;

    // Log user-friendly error message based on status
    if (statusCode === 400) {
      // Bad Request
      console.error(
        "Failed to process request. Please check your input and try again."
      );
    } else if (statusCode >= 500) {
      // Server Error
      console.error("Unable to complete action. Please try again later.");
    } else if (statusCode >= 400) {
      // Client Error
      console.error("Action could not be completed. Please try again.");
    }
  }

  return result;
};

export const candidateManagementApi = createApi({
  reducerPath: "candidateManagementApi",
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    // Candidates Management endpoint
    getCandidatesManagement: builder.query<AttorneysView, string>({
      query: (qs) => `/api/v2/candidates_management?${qs}`,
      transformResponse: (response: AttorneyResponse): AttorneysView => ({
        data: response.attorneys,
        count: response.meta?.count ?? 0
      })
    }),

    // Job Attorney Fit endpoints
    createJobAttorneyFit: builder.mutation<any, CreateJobAttorneyFitRequest>({
      query: (request) => ({
        url: "/api/v2/job_attorney_fits",
        method: "POST",
        body: request
      })
    }),
    deleteJobAttorneyFit: builder.mutation<void, { id: string | number }>({
      query: (request) => ({
        url: `/api/v2/job_attorney_fits/${request.id}`,
        method: "DELETE"
      })
    }),

    // Attorney Employer Fit endpoints
    createAttorneyEmployerFit: builder.mutation<
      any,
      CreateAttorneyEmployerFitRequest
    >({
      query: (request) => ({
        url: "/api/v2/attorney_employer_fits",
        method: "POST",
        body: request
      })
    }),
    deleteAttorneyEmployerFit: builder.mutation<void, { id: string | number }>({
      query: (request) => ({
        url: `/api/v2/attorney_employer_fits/${request.id}`,
        method: "DELETE"
      })
    })
  })
});

export const {
  // Candidates Management hooks
  useGetCandidatesManagementQuery,
  // Job Attorney Fit hooks
  useCreateJobAttorneyFitMutation,
  useDeleteJobAttorneyFitMutation,
  // Attorney Employer Fit hooks
  useCreateAttorneyEmployerFitMutation,
  useDeleteAttorneyEmployerFitMutation
} = candidateManagementApi;
